import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  useEmpresaList,
  type Empresa,
  type PapelEmpresa,
} from '../../hooks/useEmpresas'
import { EmptyState } from '../../components/feedback/EmptyState'
import { TableSkeleton } from '../../components/feedback/TableSkeleton'
import styles from '../_shared/CrudPage.module.css'

const PAGE_SIZE = 20

const PAPEIS: { value: PapelEmpresa; label: string; icon: string }[] = [
  { value: 'CLIENTE', label: 'Clientes', icon: '🏢' },
  { value: 'PLANEJADOR', label: 'Planejadores', icon: '🏛️' },
  { value: 'EXECUTOR', label: 'Executores', icon: '🔧' },
  { value: 'LABORATORIO', label: 'Laboratórios', icon: '🧪' },
]

function papelFromQuery(p: string | null): PapelEmpresa {
  const valid = PAPEIS.some((x) => x.value === p)
  return valid ? (p as PapelEmpresa) : 'CLIENTE'
}

export default function EmpresaListPage() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [busca, setBusca] = useState('')
  const [pagina, setPagina] = useState(1)

  const papel = papelFromQuery(params.get('papel'))
  const labelPapel = PAPEIS.find((p) => p.value === papel)!.label.toLowerCase()

  const { data, isLoading } = useEmpresaList({
    papeis: papel,
    search: busca || undefined,
    page: pagina,
    page_size: PAGE_SIZE,
  })

  const totalPaginas = data ? Math.ceil(data.count / PAGE_SIZE) : 1

  function trocarPapel(p: PapelEmpresa) {
    const next = new URLSearchParams(params)
    next.set('papel', p)
    setParams(next, { replace: true })
    setPagina(1)
    setBusca('')
  }

  return (
    <div className={styles.page} data-testid="empresas-list">
      <header className={styles.header}>
        <h1 className={styles.title}>Empresas</h1>
        <button
          className={styles.btnPrimary}
          onClick={() => navigate(`/empresas/novo?papel=${papel}`)}
          type="button"
          data-testid="btn-criar-empresa"
        >
          + Nova Empresa
        </button>
      </header>

      <nav
        role="tablist"
        aria-label="Filtrar empresas por papel"
        style={{
          display: 'flex',
          gap: 4,
          borderBottom: '1px solid var(--border, #e9ecef)',
          marginBottom: 12,
        }}
      >
        {PAPEIS.map((p) => {
          const ativo = p.value === papel
          return (
            <button
              key={p.value}
              type="button"
              role="tab"
              aria-selected={ativo}
              data-testid={`tab-papel-${p.value.toLowerCase()}`}
              onClick={() => trocarPapel(p.value)}
              style={{
                padding: '0.5rem 1rem',
                background: 'transparent',
                border: 'none',
                borderBottom: ativo ? '2px solid #1565C0' : '2px solid transparent',
                color: ativo ? '#1565C0' : 'var(--text-secondary, #495057)',
                fontWeight: ativo ? 600 : 400,
                fontSize: '0.9rem',
                cursor: 'pointer',
                marginBottom: -1,
              }}
            >
              {p.icon} {p.label}
            </button>
          )
        })}
      </nav>

      <div className={styles.toolbar}>
        <input
          className={styles.search}
          placeholder={`Buscar ${labelPapel} por razão social, fantasia ou CNPJ...`}
          value={busca}
          onChange={(e) => {
            setBusca(e.target.value)
            setPagina(1)
          }}
          aria-label="Buscar empresas"
          data-testid="empresas-search"
        />
      </div>

      <table className={styles.table} data-testid="table-empresas">
        <thead>
          <tr>
            <th>Razão Social</th>
            <th>Nome Fantasia</th>
            <th>CNPJ</th>
            <th>Cidade / UF</th>
            <th>Papéis</th>
          </tr>
        </thead>
        {isLoading ? (
          <TableSkeleton columns={5} testId="empresas-loading" />
        ) : (
          <tbody>
            {data?.results.map((empresa: Empresa) => (
              <tr
                key={empresa.id}
                onClick={() => navigate(`/empresas/${empresa.id}`)}
                className={styles.rowClickable}
                data-testid={`empresas-row-${empresa.id}`}
              >
                <td>
                  <strong>{empresa.razao_social}</strong>
                  {empresa.is_self && (
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: '0.7rem',
                        background: '#E3F2FD',
                        color: '#1565C0',
                        padding: '1px 6px',
                        borderRadius: 3,
                      }}
                    >
                      Sua empresa
                    </span>
                  )}
                </td>
                <td>{empresa.nome_fantasia || '—'}</td>
                <td>{empresa.cnpj || '—'}</td>
                <td>
                  {empresa.endereco_cidade
                    ? `${empresa.endereco_cidade} / ${empresa.endereco_uf}`
                    : '—'}
                </td>
                <td>
                  {empresa.papeis.map((p) => (
                    <span
                      key={p}
                      style={{
                        display: 'inline-block',
                        marginRight: 4,
                        fontSize: '0.7rem',
                        background: '#F5F5F5',
                        color: '#555',
                        padding: '1px 6px',
                        borderRadius: 3,
                      }}
                    >
                      {p}
                    </span>
                  ))}
                </td>
              </tr>
            ))}
            {data?.results.length === 0 && (
              <tr>
                <td colSpan={5}>
                  <EmptyState
                    icon={PAPEIS.find((p) => p.value === papel)!.icon}
                    title={
                      busca
                        ? `Nenhuma empresa (${labelPapel}) bate com a busca`
                        : `Nenhuma empresa cadastrada como ${labelPapel}`
                    }
                    description={
                      busca
                        ? 'Tente outro termo ou troque a aba de papel.'
                        : 'Empresas podem ter múltiplos papéis (cliente, planejador, executor, laboratório). Cadastre uma e marque os papéis que ela exerce.'
                    }
                    actionTo={busca ? undefined : `/empresas/novo?papel=${papel}`}
                    actionLabel={busca ? undefined : '+ Nova empresa'}
                  />
                </td>
              </tr>
            )}
          </tbody>
        )}
      </table>

      {!isLoading && totalPaginas > 1 && (
        <div className={styles.pagination}>
          <button
            disabled={pagina === 1}
            onClick={() => setPagina((p) => p - 1)}
            type="button"
          >
            ← Anterior
          </button>
          <span>
            Página {pagina} de {totalPaginas}
          </span>
          <button
            disabled={pagina >= totalPaginas}
            onClick={() => setPagina((p) => p + 1)}
            type="button"
          >
            Próxima →
          </button>
        </div>
      )}
    </div>
  )
}
